import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Minimize2, Video, X } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import type { User } from '@/types/api';
import { Sidebar } from '@/components/shared/Sidebar';
import { useTelemedicinaStore } from '@/stores/telemedicina-store';

/**
 * Layout de rotas autenticadas.
 *
 * ETAPA 2 / Commit 2: recuperacao de sessao via backend.
 *
 * Fluxo no F5 / nova aba:
 *   1. Component monta
 *   2. useQuery dispara GET /api/auth/me (cookie httpOnly vai junto)
 *   3. isLoading=true -> renderiza "Validando sessao..."
 *   4. 200 -> useEffect hidrata Zustand com setUser(data.user)
 *   5. 401 -> useEffect navega pra /pep-vida/login
 *
 * O beforeLoad NAO redireciona mais — se fizesse, o /me nunca rodaria
 * no caso de F5 (store vazio apos reload).
 */
export const Route = createFileRoute('/pep-vida/_authed')({
  component: AuthedLayout,
});

function AuthedLayout() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const hasUser = useAuthStore((s) => !!s.user);

  useEffect(() => {
    document.title = 'PEP-VIDA | Prontuário Eletrônico do Paciente';
  }, []);

  // PROMPT 07b / Etapa 2: revalida cookie httpOnly contra /api/auth/me.
  // NAO audit log (decisao do briefing: nao gerar ruido em cada /me).
  // NAO retry: 401 = cookie invalido/expirado -> /login.
  // staleTime 60s: revalida periodicamente, mas nao em todo render.
  const { data, isLoading, isError, isSuccess } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async (): Promise<{ user: User }> => {
      return api.get<{ user: User }>('/api/auth/me');
    },
    retry: false,
    staleTime: 60_000,
  });

  // Sucesso: hidrata store. Roda 1x quando a query resolve.
  useEffect(() => {
    if (isSuccess && data?.user) {
      setUser(data.user);
    }
  }, [isSuccess, data, setUser]);

  // Erro 401: redirect /login. Roda 1x quando a query falha.
  useEffect(() => {
    if (isError) {
      navigate({ to: '/pep-vida/login' });
    }
  }, [isError, navigate]);

  // Mostra loading enquanto valida sessao (e ainda nao tem user no store)
  if (!hasUser && isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">Validando sess&atilde;o...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-screen bg-[#f7f5fb] text-[#29233d]"
      style={{
        '--primary': '265 52% 56%',
        '--primary-foreground': '0 0% 100%',
        '--background': '260 33% 97%',
        '--foreground': '256 28% 19%',
        '--muted': '260 24% 94%',
        '--muted-foreground': '256 12% 50%',
        '--border': '260 29% 90%',
        '--input': '260 29% 90%',
        '--ring': '265 52% 56%',
      } as CSSProperties}
    >
      <Sidebar />
      <div className="flex flex-1 flex-col min-w-0">
        <main className="flex-1 overflow-auto p-3 md:p-6">
          <Outlet />
        </main>
      </div>
      <GlobalTelemedicinaWindow />
    </div>
  );
}

function GlobalTelemedicinaWindow() {
  const { url, minimized, close, toggleMinimized } = useTelemedicinaStore();
  const [position, setPosition] = useState(() => ({
    x: typeof window === 'undefined' ? 24 : Math.max(12, window.innerWidth - 454),
    y: 72,
  }));
  const drag = useRef<{ startX: number; startY: number } | null>(null);

  useEffect(() => {
    const move = (event: PointerEvent) => {
      if (!drag.current || minimized) return;
      const dx = event.clientX - drag.current.startX;
      const dy = event.clientY - drag.current.startY;
      drag.current = { startX: event.clientX, startY: event.clientY };
      setPosition((current) => ({ x: Math.max(8, current.x + dx), y: Math.max(8, current.y + dy) }));
    };
    const end = () => { drag.current = null; };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', end); };
  }, [minimized]);

  if (!url) return null;
  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button')) return;
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* compatibilidade */ }
    drag.current = { startX: event.clientX, startY: event.clientY };
  };

  return <>
    {minimized && <button type="button" className="fixed bottom-5 right-5 z-[80] grid h-12 w-12 place-items-center rounded-full bg-primary text-primary-foreground shadow-2xl" onClick={toggleMinimized} title="Restaurar teleconsulta" aria-label="Restaurar teleconsulta"><Video className="h-5 w-5" /></button>}
    <div className="fixed z-[70] overflow-hidden rounded-xl border bg-background shadow-2xl" style={{ left: position.x, top: position.y, width: minimized ? 2 : 'min(460px, calc(100vw - 16px))', height: minimized ? 2 : 'min(760px, calc(100vh - 24px))', opacity: minimized ? 0 : 1, pointerEvents: minimized ? 'none' : 'auto' }} aria-hidden={minimized}>
      <div className="flex h-11 cursor-move select-none items-center justify-between border-b bg-primary px-3 text-primary-foreground" style={{ touchAction: 'none' }} onPointerDown={startDrag}>
        <div className="flex items-center gap-2 text-sm font-semibold"><Video className="h-4 w-4" /> Telemedicina</div>
        <div className="flex items-center gap-1"><button type="button" className="grid h-8 w-8 place-items-center rounded-md hover:bg-primary-foreground/15" onClick={toggleMinimized} title="Minimizar" aria-label="Minimizar teleconsulta"><Minimize2 className="h-4 w-4" /></button><button type="button" className="grid h-8 w-8 place-items-center rounded-md hover:bg-primary-foreground/15" onClick={close} title="Fechar" aria-label="Fechar teleconsulta"><X className="h-4 w-4" /></button></div>
      </div>
      <iframe title="Telemedicina" src={url} allow="camera; microphone; autoplay; fullscreen; display-capture" className="h-[calc(100%-2.75rem)] w-full border-0" />
    </div>
  </>;
}
